import stringify from 'fast-safe-stringify'
import { useState } from 'react'

import { AstraInputWrapper, AstraParagraph, AstraSelect, AstraSwitch, AstraTextField } from '~/astra'
import { isNullish } from '~/utils/guards'

import { ExpertConfigSection } from '../../Form/ExpertConfigSection'
import { withFieldGroup } from '../../Form/formHooks'
import { FieldGroupWrapper } from '../../Form/FormWrapper'
import { createFormFieldMetaProps, parseIntOrZero } from '../../Form/utils'
import { DEFAULT_LOCATION_STAFFING_CONFIG, defaultProfile, type FormLocationStaffingConfig, locationStaffingConfigSchema } from './formState'

const AUTO_ROOM_TYPE = 'auto'
const roomTypeOptions = [
    { label: 'Automatisk (fra omsorgsnivå)', value: AUTO_ROOM_TYPE },
    { label: 'Operasjonsstue (O)', value: 'O' },
    { label: 'Dagkirurgi (D)', value: 'D' },
] as const

const defaultValues = defaultProfile

const toRawJson = (config: FormLocationStaffingConfig | undefined | null) => stringify(config, undefined, 2)

type LocationStaffingConfigFieldsProps = {
    size?: 'compact' | 'default'
}

export const LocationStaffingConfigFields = withFieldGroup({
    defaultValues,
    props: {
        size: 'default',
    } as LocationStaffingConfigFieldsProps,
    render: function Render({ group, size }) {
        const [mode, setMode] = useState<'visual' | 'raw'>('visual')
        const [rawJsonError, setRawJsonError] = useState<string | undefined>()

        const isRawMode = mode === 'raw'

        const handleModeChange = (nextMode: typeof mode) => {
            setMode(nextMode)
            setRawJsonError(undefined)
        }

        const handleRawJsonChange = (value: string) => {
            try {
                const parsed = locationStaffingConfigSchema.parse(JSON.parse(value))
                group.setFieldValue('extraStaffingConfig', parsed)
                setRawJsonError(undefined)
            } catch (e) {
                const errorMessage = e instanceof Error ? e.message : 'Ukjent feil'
                setRawJsonError(`Ugyldig JSON eller feil format: ${errorMessage}`)
            }
        }

        return (
            <group.AppField name="extraStaffingConfig">
                {rootField => (
                    <ExpertConfigSection
                        title="Bemanningskonfigurasjon"
                        description="Overstyrer verdier i extra_staffing_config for denne stueprofilen."
                        mode={mode}
                        onModeChange={handleModeChange}
                        size={size}
                    >
                        <label className="flex w-fit cursor-pointer items-center gap-2">
                            <AstraSwitch
                                checked={!isNullish(rootField.state.value)}
                                onCheckedChange={checked => rootField.handleChange(checked ? DEFAULT_LOCATION_STAFFING_CONFIG : null)}
                            />
                            <AstraParagraph>Inkluder stueprofilen i generering av bemanningsplan</AstraParagraph>
                        </label>

                        {isNullish(rootField.state.value) ? null : isRawMode ? (
                            <AstraInputWrapper error={!!rawJsonError} errorText={rawJsonError}>
                                <textarea
                                    className="h-32 w-full bg-transparent outline-none"
                                    defaultValue={toRawJson(rootField.state.value)}
                                    onChange={e => handleRawJsonChange(e.target.value)}
                                />
                            </AstraInputWrapper>
                        ) : (
                            <>
                                <FieldGroupWrapper>
                                    <group.AppField name="extraStaffingConfig.required_surgeons_min">
                                        {field => (
                                            <AstraTextField
                                                type="number"
                                                min={0}
                                                label="Min antall kirurger"
                                                value={String(field.state.value)}
                                                onChange={e => field.handleChange(parseIntOrZero(e.target.value))}
                                                {...createFormFieldMetaProps(field)}
                                                fullWidth
                                            />
                                        )}
                                    </group.AppField>
                                    <group.AppField name="extraStaffingConfig.required_surgeons_max">
                                        {field => (
                                            <AstraTextField
                                                type="number"
                                                min={0}
                                                label="Maks antall kirurger"
                                                value={String(field.state.value)}
                                                onChange={e => field.handleChange(parseIntOrZero(e.target.value))}
                                                {...createFormFieldMetaProps(field)}
                                                fullWidth
                                            />
                                        )}
                                    </group.AppField>
                                </FieldGroupWrapper>
                                <group.AppField name="extraStaffingConfig.room_type">
                                    {field => (
                                        <AstraSelect
                                            label="Stuetype"
                                            options={roomTypeOptions}
                                            value={field.state.value ?? AUTO_ROOM_TYPE}
                                            onChange={value => field.handleChange(value === AUTO_ROOM_TYPE ? null : (value as 'O' | 'D'))}
                                            {...createFormFieldMetaProps(field)}
                                        />
                                    )}
                                </group.AppField>
                            </>
                        )}
                    </ExpertConfigSection>
                )}
            </group.AppField>
        )
    },
})
